import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ApiService } from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { formatDateTime } from '../utils/datetime';
import { useToast } from './Toast';
import { 
  X, 
  Download, 
  Eye, 
  Calendar, 
  User, 
  Mail, 
  Phone, 
  Building, 
  CreditCard, 
  Image as ImageIcon,
  QrCode,
  CheckCircle,
  XCircle,
  Clock,
  Printer,
  ExternalLink,
  Lock,
  Receipt
} from 'lucide-react';
import ConfirmationDialog from './ui/ConfirmationDialog';
import UserDetailsEditor from './UserDetailsEditor';
import LockerAssignModal from './LockerAssignModal';
import AddRegistrationDocument from './AddRegistrationDocument';
import { useAuth } from '../contexts/AuthContext';
import PendingChangePanel from './PendingChangePanel';
import RegistrationHistory from './RegistrationHistory';
import RefundDialog from './RefundDialog';

const RegistrationDetailsModal = ({ isOpen, onClose, eventId, userId, onStatusChanged }) => {
  const [showBadgePreview, setShowBadgePreview] = useState(false);
  const [badgeBlob, setBadgeBlob] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showLockerModal, setShowLockerModal] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [receiptForm, setReceiptForm] = useState(null); // { amount, paidAt, plain } while the dialog is open
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const { isAdmin, isEditor } = useAuth();
  const canEditDocuments = isAdmin() || isEditor();

  // Fetch registration details
  const { data: registration, isLoading, error } = useQuery({
    queryKey: ['registration', eventId, userId],
    queryFn: () => ApiService.getRegistration(eventId, userId),
    enabled: isOpen && !!eventId && !!userId,
  });

  // Fetch the event to get the form schema, so we can edit/add any field
  // (including optional ones the registrant left blank).
  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: isOpen && !!eventId,
  });

  const updateRegistrationMutation = useMutation({
    mutationFn: (formData) => ApiService.updateRegistration(eventId, userId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['registration', eventId, userId]);
      queryClient.invalidateQueries(['registrations', eventId]);
      showSuccess('Registration details updated successfully!');
    },
    onError: (err) => {
      showError(err?.message || 'Failed to update registration details.');
    }
  });

  const sendConfirmationEmailMutation = useMutation({
    mutationFn: () => ApiService.sendRegistrationConfirmationEmails(eventId, [userId]),
    onSuccess: () => showSuccess('Confirmation email sent successfully!'),
    onError: (err) => showError(err?.message || 'Failed to send confirmation email.'),
  });

  const recheckPaymentMutation = useMutation({
    mutationFn: () => ApiService.recheckRegistrationPayment(eventId, userId),
    onSuccess: (result) => {
      queryClient.invalidateQueries(['registration', eventId, userId]);
      queryClient.invalidateQueries(['registrations', eventId]);
      onStatusChanged?.();
      const latest = result.orders?.[0];
      if (result.registrationStatus === 'REGISTERED' || result.registrationStatus === 'PENDING_FOR_APPROVAL') {
        showSuccess(`Payment found${latest?.paymentId ? ` (${latest.paymentId})` : ''}. Registration is now ${result.registrationStatus.replace(/_/g, ' ').toLowerCase()}.`);
      } else if (!latest) {
        showError('No online payment was ever started for this registration.');
      } else {
        showError(`No successful payment. Latest attempt ${latest.orderId} is ${latest.status.toLowerCase()} (${latest.provider}).`);
      }
    },
    onError: (err) => showError(err?.message || 'Could not check the payment.'),
  });

  const approveRegistrationMutation = useMutation({
    mutationFn: () => ApiService.approveRegistration(eventId, userId, { reason: 'Approved from details view' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['registration', eventId, userId]);
      queryClient.invalidateQueries(['registrations', eventId]);
      onStatusChanged?.();
      showSuccess('Registration approved successfully!');
    },
    onError: (err) => {
      showError(err?.message || 'Failed to approve registration.');
    }
  });

  const rejectRegistrationMutation = useMutation({
    mutationFn: (reason) => ApiService.rejectRegistration(eventId, userId, { reason: reason || 'Rejected from details view' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['registration', eventId, userId]);
      queryClient.invalidateQueries(['registrations', eventId]);
      onStatusChanged?.();
      showSuccess('Registration rejected successfully!');
      setShowRejectDialog(false);
      setRejectReason('');
    },
    onError: (err) => {
      showError(err?.message || 'Failed to reject registration.');
    }
  });

  // Fetch badge preview
  const { data: badgeData, isLoading: badgeLoading } = useQuery({
    queryKey: ['badge', eventId, userId],
    queryFn: () => ApiService.printUserBadge(eventId, userId, registration),
    enabled: showBadgePreview && !!registration,
  });

  useEffect(() => {
    if (badgeData) {
      setBadgeBlob(badgeData);
    }
  }, [badgeData]);

  const handleDownloadQR = () => {
    if (registration?.qrCodeBase64) {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${registration.qrCodeBase64}`;
      link.download = `qr-code-${userId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showSuccess('QR code downloaded successfully!');
    }
  };

  const isPaidEvent = String(event?.eventType || '').toLowerCase() === 'paid';

  const openReceiptDialog = () => setReceiptForm({
    amount: registration?.calculatedAmount > 0 ? String(registration.calculatedAmount) : '',
    paidAt: '',
    plain: false,
  });

  const handleDownloadReceipt = async () => {
    setIsDownloadingReceipt(true);
    try {
      const blob = await ApiService.downloadPaymentReceipt(eventId, userId, {
        amount: receiptForm.amount,
        paidAt: receiptForm.paidAt ? receiptForm.paidAt.replace('T', ' ') : '',
        plain: receiptForm.plain,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = blob.type === 'application/zip' ? `${userId}_payment_documents.zip` : `${userId}_payment_receipt.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setReceiptForm(null);
      showSuccess('Receipt downloaded');
    } catch (err) {
      showError(err.message || 'Failed to generate the receipt');
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  const handleDownloadBadge = () => {
    if (badgeBlob) {
      const url = window.URL.createObjectURL(badgeBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `badge-${userId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess('Badge downloaded successfully!');
    }
  };

  const handleDownloadDocument = (documentType, url) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${documentType}-${userId}`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSuccess(`${documentType} downloaded successfully!`);
  };

  const handleViewImage = (url) => {
    setSelectedImage(url);
    setShowImagePreview(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      REGISTERED: { label: 'Registered', className: 'bg-green-100 text-green-800' },
      PENDING_PAYMENT: { label: 'Pending Payment', className: 'bg-yellow-100 text-yellow-800' },
      PENDING_FOR_APPROVAL: { label: 'Pending Approval', className: 'bg-orange-100 text-orange-800' },
      CANCELLED: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
      PAYMENT_FAILED: { label: 'Payment Failed', className: 'bg-red-100 text-red-800' }
    };
    
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <Badge className={config.className}>
        {config.label}
      </Badge>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Registration Details</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {(registration?.status === 'PENDING_PAYMENT' || registration?.status === 'PAYMENT_FAILED') && (
                <Button
                  variant="outline"
                  className="text-blue-700 border-blue-300 hover:bg-blue-50"
                  onClick={() => recheckPaymentMutation.mutate()}
                  disabled={recheckPaymentMutation.isPending}
                  title="Ask Cashfree/PayU for the latest status of this registration's payment"
                >
                  {recheckPaymentMutation.isPending ? 'Checking…' : 'Recheck payment'}
                </Button>
              )}
              {registration?.status === 'PENDING_FOR_APPROVAL' && (
                <>
                  <Button
                    variant="outline"
                    className="text-green-700 border-green-300 hover:bg-green-50"
                    onClick={() => approveRegistrationMutation.mutate()}
                    disabled={approveRegistrationMutation.isPending || rejectRegistrationMutation.isPending}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-700 border-red-300 hover:bg-red-50"
                    onClick={() => setShowRejectDialog(true)}
                    disabled={approveRegistrationMutation.isPending || rejectRegistrationMutation.isPending}
                  >
                    Reject
                  </Button>
                </>
              )}
              {event?.lockerEnabled && (
                <Button
                  variant="outline"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => setShowLockerModal(true)}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Manage Lockers
                </Button>
              )}
              {isAdmin() && isPaidEvent && registration?.calculatedAmount > 0
                && !['PENDING_PAYMENT', 'PAYMENT_FAILED'].includes(registration?.status) && (
                <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => setShowRefund(true)}>
                  Process refund
                </Button>
              )}
              {canEditDocuments && isPaidEvent && registration && (
                <Button variant="outline" onClick={openReceiptDialog}>
                  <Receipt className="w-4 h-4 mr-2" />
                  Download Receipt
                </Button>
              )}
              {event?.showConfirmationEmailButton !== false && (
                <Button
                  variant="outline"
                  className="text-blue-700 border-blue-300 hover:bg-blue-50"
                  onClick={() => sendConfirmationEmailMutation.mutate()}
                  disabled={sendConfirmationEmailMutation.isPending}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendConfirmationEmailMutation.isPending ? 'Sending…' : 'Send Confirmation Email'}
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">Error loading registration details</p>
            </div>
          ) : registration ? (
            <div className="space-y-6">
              {registration.pendingChange && (
                <PendingChangePanel
                  eventId={eventId}
                  userId={userId}
                  pendingChange={registration.pendingChange}
                  currency={event?.currency}
                  onDone={onStatusChanged}
                />
              )}
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <User className="w-5 h-5" />
                    <span>Basic Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                      <p className="text-sm text-gray-900">{registration.userId}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <div>{getStatusBadge(registration.status)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                      <p className="text-sm text-gray-900">{registration.source || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Registered At</label>
                      <p className="text-sm text-gray-900">
                        {formatDateTime(registration.registeredAt, 'N/A')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment ID</label>
                      <p className="text-sm text-gray-900">{registration.paymentId || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Calculated Amount</label>
                      <p className="text-sm text-gray-900">
                        {registration.calculatedAmount != null ? formatCurrency(registration.calculatedAmount, registration.currency || 'INR') : 'N/A'}
                      </p>
                      {registration.couponCode && (
                        <p className="text-xs text-green-700 mt-1">
                          Coupon {registration.couponCode}
                          {registration.discountAmount != null ? ` (−${formatCurrency(registration.discountAmount, registration.currency || 'INR')})` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Form Data — editable so admins can fill/update any field,
                  including optional ones the registrant left blank. */}
              <UserDetailsEditor
                userData={registration}
                formSchema={event?.formSchema || []}
                onSave={(formData) => updateRegistrationMutation.mutate(formData)}
                isLoading={updateRegistrationMutation.isPending}
              />

              {/* Documents */}
              {(canEditDocuments || (registration.documents && Object.keys(registration.documents).length > 0)) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <ImageIcon className="w-5 h-5" />
                      <span>Documents</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(registration.documents || {}).map(([documentType, url]) => (
                        <div key={documentType} className="border rounded-lg p-4 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <h4 className="font-medium text-gray-900 truncate min-w-0" title={documentType}>
                              {documentType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </h4>
                            <div className="flex space-x-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewImage(url)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadDocument(documentType, url)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 truncate" title={typeof url === 'string' ? url : ''}>
                            {typeof url === 'string' ? url : JSON.stringify(url)}
                          </div>
                        </div>
                      ))}
                    </div>
                    {canEditDocuments && (
                      <div className="mt-4 border-t pt-4">
                        <AddRegistrationDocument eventId={eventId} userId={registration.userId} />
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* QR Code */}
              {registration.qrCodeBase64 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <QrCode className="w-5 h-5" />
                      <span>QR Code</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-4">
                      <div className="border rounded-lg p-4">
                        <img
                          src={`data:image/png;base64,${registration.qrCodeBase64}`}
                          alt="QR Code"
                          className="w-32 h-32"
                        />
                      </div>
                      <div>
                        <Button onClick={handleDownloadQR} className="mb-2">
                          <Download className="w-4 h-4 mr-2" />
                          Download QR Code
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pricing Breakdown */}
              {registration.pricingBreakdown && Object.keys(registration.pricingBreakdown).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <CreditCard className="w-5 h-5" />
                      <span>Pricing Breakdown</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(registration.pricingBreakdown).map(([field, detail]) => (
                        <div key={field} className="flex justify-between items-center border-b pb-2">
                          <div>
                            <p className="font-medium">{detail.fieldLabel}</p>
                            <p className="text-sm text-gray-600">{detail.selectedOption}</p>
                          </div>
                          <p className="font-medium">{formatCurrency(detail.price, registration.currency || 'INR')}</p>
                        </div>
                      ))}
                      <div className="flex justify-between items-center font-bold text-lg border-t pt-2">
                        <span>Total</span>
                        <span>{formatCurrency(registration.calculatedAmount, registration.currency || 'INR')}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Check-ins */}
              {registration.checkins && Object.keys(registration.checkins).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>Check-ins</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(registration.checkins).map(([purpose, checkin]) => (
                        <div key={purpose} className="flex justify-between items-center border rounded-lg p-3">
                          <div>
                            <p className="font-medium">{purpose}</p>
                            <p className="text-sm text-gray-600">
                              {checkin.used ? 'Used' : 'Not Used'}
                            </p>
                          </div>
                          <div className="text-right">
                            {checkin.usedAt && (
                              <p className="text-sm text-gray-600">
                                {formatDateTime(checkin.usedAt)}
                                {checkin.scannedBy && <span className="text-gray-500"> · by {checkin.scannedBy}</span>}
                              </p>
                            )}
                            <div className="flex items-center space-x-1">
                              {checkin.used ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <Clock className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {registration.inviteName && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">
                  Registered through the invite link for <b>{registration.inviteName}</b>
                </div>
              )}

              <RegistrationHistory eventId={eventId} userId={userId} currency={event?.currency} />
              {showRefund && (
                <RefundDialog eventId={eventId} userId={userId} onClose={() => setShowRefund(false)} onRefunded={() => onStatusChanged?.()} />
              )}

              {/* Approval Information */}
              {(registration.approvedBy || registration.rejectedBy || registration.cancelledBy) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <User className="w-5 h-5" />
                      <span>Approval Information</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {registration.approvedBy && (
                        <div className="flex items-center space-x-2 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>Approved by {registration.approvedBy}</span>
                          {registration.approvalDate && (
                            <span className="text-sm text-gray-600">
                              on {formatDateTime(registration.approvalDate)}
                            </span>
                          )}
                        </div>
                      )}
                      {registration.rejectedBy && (
                        <div className="flex items-center space-x-2 text-red-600">
                          <XCircle className="w-4 h-4" />
                          <span>Rejected by {registration.rejectedBy}</span>
                          {registration.rejectionDate && (
                            <span className="text-sm text-gray-600">
                              on {formatDateTime(registration.rejectionDate)}
                            </span>
                          )}
                        </div>
                      )}
                      {registration.cancelledBy && (
                        <div className="flex items-center space-x-2 text-orange-600">
                          <XCircle className="w-4 h-4" />
                          <span>Cancelled by {registration.cancelledBy}</span>
                          {registration.cancellationDate && (
                            <span className="text-sm text-gray-600">
                              on {formatDateTime(registration.cancellationDate)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Badge Preview */}
              {/* <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Printer className="w-5 h-5" />
                    <span>Badge Preview</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-4">
                    <Button 
                      onClick={() => setShowBadgePreview(!showBadgePreview)}
                      disabled={badgeLoading}
                    >
                      {badgeLoading ? 'Loading...' : showBadgePreview ? 'Hide Badge' : 'Show Badge'}
                    </Button>
                    {badgeBlob && (
                      <Button onClick={handleDownloadBadge}>
                        <Download className="w-4 h-4 mr-2" />
                        Download Badge
                      </Button>
                    )}
                  </div>
                  
                  {showBadgePreview && badgeBlob && (
                    <div className="mt-4">
                      <iframe
                        src={URL.createObjectURL(badgeBlob)}
                        className="w-full h-96 border rounded-lg"
                        title="Badge Preview"
                      />
                    </div>
                  )}
                </CardContent>
              </Card> */}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">No registration data found</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {showImagePreview && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Image Preview</h3>
                <Button variant="outline" onClick={() => setShowImagePreview(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex justify-center">
                <img
                  src={selectedImage}
                  alt="Document Preview"
                  className="max-w-full max-h-[70vh] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={showRejectDialog}
        onClose={() => {
          setShowRejectDialog(false);
          setRejectReason('');
        }}
        onConfirm={() => rejectRegistrationMutation.mutate(rejectReason)}
        title="Reject Registration"
        message={`Are you sure you want to reject the registration for ${userId}?`}
        confirmText="Reject"
        variant="danger"
        isLoading={rejectRegistrationMutation.isPending}
        inputLabel="Rejection Reason (optional)"
        inputPlaceholder="Enter reason for rejection…"
        inputValue={rejectReason}
        onInputChange={setRejectReason}
      />

      {/* Locker Management Modal (opened from the CTA above) */}
      {receiptForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Download payment receipt</h3>
              <Button variant="ghost" size="sm" onClick={() => setReceiptForm(null)} disabled={isDownloadingReceipt}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-1">
              <label htmlFor="receipt-amount" className="text-sm font-medium text-gray-700">Amount (Rs.)</label>
              <input
                id="receipt-amount"
                type="number"
                min="1"
                step="any"
                value={receiptForm.amount}
                onChange={(e) => setReceiptForm((prev) => ({ ...prev, amount: e.target.value }))}
                placeholder="Leave blank to use the registration amount"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="receipt-paid-at" className="text-sm font-medium text-gray-700">Payment date &amp; time (IST)</label>
              <input
                id="receipt-paid-at"
                type="datetime-local"
                value={receiptForm.paidAt}
                onChange={(e) => setReceiptForm((prev) => ({ ...prev, paidAt: e.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500">Leave blank to use the registration time.</p>
            </div>
            {(
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={receiptForm.plain}
                  onChange={(e) => setReceiptForm((prev) => ({ ...prev, plain: e.target.checked }))}
                />
                Only the payment receipt (skip the GST invoices and refund notes)
              </label>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setReceiptForm(null)} disabled={isDownloadingReceipt}>Cancel</Button>
              <Button onClick={handleDownloadReceipt} disabled={isDownloadingReceipt}>
                <Download className="w-4 h-4 mr-2" />
                {isDownloadingReceipt ? 'Generating…' : 'Download'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <LockerAssignModal
        open={showLockerModal}
        onClose={() => setShowLockerModal(false)}
        eventId={eventId}
        userId={userId}
      />
    </div>
  );
};

export default RegistrationDetailsModal;
